import { useEffect } from "react";

/**
 * Sets per-page <title>, <meta name="description">, <meta name="keywords">,
 * and OpenGraph / Twitter equivalents, restoring the sitewide defaults on unmount.
 */
const DEFAULT_TITLE =
  "TopClass Imóveis | Alto Padrão no Rio de Janeiro";
const DEFAULT_DESC =
  "Apartamentos à venda com vista para a Lagoa Rodrigo de Freitas — 2 e 3 quartos, mobiliados, alto padrão, Avenida Epitácio Pessoa. Curadoria TopClass Imóveis.";
const DEFAULT_KEYWORDS =
  "Topclass Imóveis, Imobiliária premium Rio de Janeiro, Apartamentos alto padrão RJ, Coberturas Rio de Janeiro, Casas Barra da Tijuca, Imóveis Zona Sul, Imóveis Leblon Ipanema Copacabana";
const DEFAULT_OG_TITLE =
  "Topclass Imobiliária Premium | Apartamentos e Coberturas na Barra da Tijuca e Zona Sul";
const DEFAULT_OG_DESC =
  "Imóveis premium à venda na Barra da Tijuca, Zona Sul e Tijuca. Apartamentos, coberturas e casas de alto padrão no Rio de Janeiro. Atendimento exclusivo.";

function upsertMetaByName(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogType = "website",
  image,
  noIndex = false,
}: {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  image?: string;
  noIndex?: boolean;
}) {
  useEffect(() => {
    if (title) document.title = title.slice(0, 70);
    if (description) upsertMetaByName("description", description.slice(0, 160));
    if (keywords) upsertMetaByName("keywords", keywords.slice(0, 250));

    const finalOgTitle = (ogTitle || title || "").slice(0, 95);
    const finalOgDesc = (ogDescription || description || "").slice(0, 200);

    if (finalOgTitle) {
      upsertMetaByProperty("og:title", finalOgTitle);
      upsertMetaByName("twitter:title", finalOgTitle);
    }
    if (finalOgDesc) {
      upsertMetaByProperty("og:description", finalOgDesc);
      upsertMetaByName("twitter:description", finalOgDesc);
    }
    upsertMetaByProperty("og:type", ogType);
    if (image) {
      upsertMetaByProperty("og:image", image);
      upsertMetaByName("twitter:image", image);
    }
    upsertMetaByName("robots", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large");

    return () => {
      document.title = DEFAULT_TITLE;
      upsertMetaByName("description", DEFAULT_DESC);
      upsertMetaByName("keywords", DEFAULT_KEYWORDS);
      upsertMetaByProperty("og:title", DEFAULT_OG_TITLE);
      upsertMetaByProperty("og:description", DEFAULT_OG_DESC);
      upsertMetaByName("twitter:title", DEFAULT_OG_TITLE);
      upsertMetaByName("twitter:description", DEFAULT_OG_DESC);
      upsertMetaByProperty("og:type", "website");
      upsertMetaByName("robots", "index, follow, max-image-preview:large");
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogType, image, noIndex]);
}
